import { lazy } from "react";
import type { RouteObject } from "react-router-dom";
import UnderDevelopment from "../../features/shared/pages/UnderDevelopment";
import { APP_ROUTES } from "../routePaths";
import { createProtectedIndexRoute, withSuspense } from "./helpers";

const ForwardingRequestsPage = lazy(() =>
  import("../../features/forwarding-requests/pages/ForwardingRequestsPage").then((m) => ({ default: m.default }))
);

const PreAlertReportPage = lazy(() =>
  import("../../features/pre-alert-report/pages/PreAlertReportPage").then((m) => ({ default: m.default }))
);

const PreAlertReportViewPage = lazy(() =>
  import("../../features/pre-alert-report/pages/PreAlertReportViewPage").then((m) => ({ default: m.default }))
);

const PreAlertF5ViewPage = lazy(() =>
  import("../../features/pre-alert-report/pages/PreAlertF5ViewPage").then((m) => ({ default: m.default }))
);

const SaleCurrencyViewPage = lazy(() =>
  import("../../features/sale-order/pages/SaleCurrencyViewPage").then((m) => ({ default: m.default }))
);

const SaleEmployeeViewPage = lazy(() =>
  import("../../features/sale-order/pages/SaleEmployeeViewPage").then((m) => ({ default: m.default }))
);

const SalePortViewPage = lazy(() =>
  import("../../features/sale-order/pages/SalePortViewPage").then((m) => ({ default: m.default }))
);

const SaleCustomerViewPage = lazy(() =>
  import("../../features/sale-order/pages/SaleCustomerViewPage").then((m) => ({ default: m.default }))
);

const VesselActivityReportPage = lazy(() =>
  import("../../features/sale-order/pages/VesselActivityReportPage").then((m) => ({ default: m.default }))
);

const MissingRemarksReportPage = lazy(() =>
  import("../../features/sale-order/pages/MissingRemarksReportPage").then((m) => ({ default: m.default }))
);

const ReportFilterPage = lazy(() =>
  import("../../features/reports/pages/ReportFilterPage").then((m) => ({ default: m.default }))
);

/**
 * The Report menu's filter-and-View reports (master lists, sale and customer reports), all drawn by
 * ReportFilterPage. The key gives each report its own page state: without it React would carry
 * one report's filters into the next.
 */
const reportFilterRoutes = [
  [APP_ROUTES.addressReport, "address"],
  [APP_ROUTES.agentReport, "agent"],
  [APP_ROUTES.customerReport, "customer"],
  [APP_ROUTES.driverReport, "driver"],
  [APP_ROUTES.employeeReport, "employee"],
  [APP_ROUTES.itemReport, "item"],
  [APP_ROUTES.productReport, "product"],
  [APP_ROUTES.supplierReport, "supplier"],
  [APP_ROUTES.saleReport, "sale"],
  [APP_ROUTES.saleDetailedReport, "sale-detailed"],
  [APP_ROUTES.saleCreditReport, "sale-credit"],
  [APP_ROUTES.saleCreditDetailedReport, "sale-credit-detailed"],
  [APP_ROUTES.customerBalanceReport, "customer-balance"],
  [APP_ROUTES.customerAgeingReport, "customer-ageing"],
  [APP_ROUTES.customerReceiptReport, "customer-receipt"],
  [APP_ROUTES.customerReceiptDueReport, "customer-receipt-due"],
  [APP_ROUTES.customerQuotationReport, "customer-quotation"],
  [APP_ROUTES.supplierBalanceReport, "supplier-balance"],
  [APP_ROUTES.supplierPaymentReport, "supplier-payment"],
  [APP_ROUTES.supplierPaymentDueReport, "supplier-payment-due"],
  [APP_ROUTES.supplierStatementReport, "supplier-statement"],
  [APP_ROUTES.purchaseReport, "purchase"],
  [APP_ROUTES.purchaseDetailedReport, "purchase-detailed"],
  [APP_ROUTES.purchaseItemWiseReport, "purchase-item-wise"],
  [APP_ROUTES.billsReport, "bills"],
  [APP_ROUTES.billsDetailedReport, "bills-detailed"],
  [APP_ROUTES.billsItemWiseReport, "bills-item-wise"],
  [APP_ROUTES.expenseReport, "expense"],
  [APP_ROUTES.paymentVoucherReport, "payment-voucher"],
  [APP_ROUTES.salaryReport, "salary"],
  [APP_ROUTES.employeeSalaryReport, "employee-salary"],
  [APP_ROUTES.fuelReport, "fuel"],
  [APP_ROUTES.renewalReport, "renewal"],
] as const;

const genericReportRoutePaths = [
  "/Report/TruckReport",
  "/Report/TollReport",
] as const;

const CustomerStatementPage = lazy(() =>
  import("../../features/customer-statement/pages/CustomerStatementPage").then((m) => ({ default: m.default }))
);
const SendStatementsPage = lazy(() =>
  import("../../features/customer-statement/pages/SendStatementsPage").then((m) => ({ default: m.default }))
);

const DriverRTIReportPage = lazy(() =>
  import("../../features/reports/pages/DriverRTIReportPage").then((m) => ({ default: m.default }))
);

const ForwardingReportPage = lazy(() =>
  import("../../features/reports/pages/ForwardingReportPage").then((m) => ({ default: m.default }))
);

const SaleOrderForwardingReportPage = lazy(() =>
  import("../../features/sale-order-forwarding-report/pages/SaleOrderForwardingReportPage").then(
    (m) => ({ default: m.default })
  )
);

export const reportRoutes: RouteObject[] = [
  createProtectedIndexRoute(
    APP_ROUTES.forwardingRequests,
    withSuspense(<ForwardingRequestsPage />)
  ),
  createProtectedIndexRoute(
    APP_ROUTES.myForwardingRequests,
    withSuspense(<ForwardingRequestsPage mine />)
  ),
  createProtectedIndexRoute(
    APP_ROUTES.preAlertReport,
    withSuspense(<PreAlertReportPage />)
  ),
  createProtectedIndexRoute(
    APP_ROUTES.preAlertReportView,
    withSuspense(<PreAlertReportViewPage />)
  ),
  createProtectedIndexRoute(
    APP_ROUTES.preAlertF5View,
    withSuspense(<PreAlertF5ViewPage />)
  ),
  createProtectedIndexRoute(
    "/Report/SaleCurrencyView",
    withSuspense(<SaleCurrencyViewPage />)
  ),
  createProtectedIndexRoute(
    APP_ROUTES.saleEmployeeView,
    withSuspense(<SaleEmployeeViewPage />)
  ),
  createProtectedIndexRoute(
    "/Report/DriverRTIReport",
    withSuspense(<DriverRTIReportPage />)
  ),
  createProtectedIndexRoute(
    APP_ROUTES.salePortView,
    withSuspense(<SalePortViewPage />)
  ),
  createProtectedIndexRoute(
    "/Report/SaleCustomerView",
    withSuspense(<SaleCustomerViewPage />)
  ),
  createProtectedIndexRoute(
    APP_ROUTES.vesselActivityReport,
    withSuspense(<VesselActivityReportPage />)
  ),
  createProtectedIndexRoute(
    APP_ROUTES.missingRemarksReport,
    withSuspense(<MissingRemarksReportPage />)
  ),
  createProtectedIndexRoute(
    APP_ROUTES.forwardingReport,
    withSuspense(<ForwardingReportPage />)
  ),
  createProtectedIndexRoute(
    APP_ROUTES.saleOrderForwardingReport,
    withSuspense(<SaleOrderForwardingReportPage />)
  ),
  // The path the legacy screen lived at, kept so existing links still land.
  createProtectedIndexRoute(
    APP_ROUTES.saleOrderForwardingReportLegacy,
    withSuspense(<SaleOrderForwardingReportPage />)
  ),
  createProtectedIndexRoute(APP_ROUTES.customerStatement, withSuspense(<CustomerStatementPage />)),
  createProtectedIndexRoute(APP_ROUTES.customerStatementSend, withSuspense(<SendStatementsPage />)),
  createProtectedIndexRoute(APP_ROUTES.customerStatementConversation(), withSuspense(<SendStatementsPage />)),
  ...reportFilterRoutes.map(([path, page]) =>
    createProtectedIndexRoute(path, withSuspense(<ReportFilterPage key={page} page={page} />))
  ),
  ...genericReportRoutePaths.map((path) =>
    createProtectedIndexRoute(path, <UnderDevelopment />)
  ),
];
